import React, { useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    CircularProgress,
    Typography,
    Alert,
} from "@mui/material";
import { PaymentTransaction } from "../../types";
import { formatCurrencyBDT } from "../../utils/currency";

export interface RefundBkashPaymentRequest {
    refund_amount: number;
    reason: string;
    sku?: string;
}

interface RefundBkashPaymentDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (data: RefundBkashPaymentRequest) => Promise<void>;
    transaction: PaymentTransaction | null;
}

const defaultForm = (): RefundBkashPaymentRequest => ({
    refund_amount: 0,
    reason: "",
    sku: "",
});

export const RefundBkashPaymentDialog: React.FC<
    RefundBkashPaymentDialogProps
> = ({ open, onClose, onConfirm, transaction }) => {
    const [form, setForm] = useState<RefundBkashPaymentRequest>(defaultForm);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const originalAmount = transaction ? Number(transaction.amount) : 0;

    const handleOpen = () => {
        setForm({ ...defaultForm(), refund_amount: originalAmount });
        setError(null);
    };

    const handleClose = () => {
        if (saving) return;
        setForm(defaultForm());
        setError(null);
        onClose();
    };

    const handleConfirm = async () => {
        if (form.refund_amount <= 0) {
            setError("Refund amount must be greater than 0.");
            return;
        }
        if (form.refund_amount > originalAmount) {
            setError(
                `Refund amount cannot exceed the original payment (${formatCurrencyBDT(originalAmount)}).`,
            );
            return;
        }
        if (!form.reason.trim()) {
            setError("A reason is required.");
            return;
        }
        setSaving(true);
        setError(null);
        try {
            await onConfirm({
                refund_amount: form.refund_amount,
                reason: form.reason.trim(),
                sku: form.sku?.trim() || undefined,
            });
            setForm(defaultForm());
            onClose();
        } catch (err: any) {
            setError(
                err?.response?.data?.detail ||
                    err?.message ||
                    "Failed to refund payment. Please try again.",
            );
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            TransitionProps={{ onEnter: handleOpen }}
        >
            <DialogTitle>Refund bKash Payment</DialogTitle>
            <DialogContent>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                    <Alert severity="info" sx={{ fontSize: "0.8125rem" }}>
                        This calls bKash's real Refund API and sends the money
                        back to the customer's wallet — it can't be undone.
                        {transaction?.reference_number && (
                            <>
                                {" "}
                                Original transaction:{" "}
                                <strong>{transaction.reference_number}</strong>
                            </>
                        )}
                    </Alert>
                    <TextField
                        label="Refund Amount"
                        type="number"
                        value={form.refund_amount}
                        onChange={(e) =>
                            setForm({
                                ...form,
                                refund_amount: Number(e.target.value),
                            })
                        }
                        fullWidth
                        required
                        inputProps={{ min: 0, max: originalAmount, step: 0.01 }}
                        helperText={`Original payment: ${formatCurrencyBDT(originalAmount)} — leave as-is for a full refund, or lower it for a partial refund.`}
                        error={!!error && form.refund_amount <= 0}
                    />
                    <TextField
                        label="Reason"
                        value={form.reason}
                        onChange={(e) =>
                            setForm({ ...form, reason: e.target.value })
                        }
                        fullWidth
                        required
                        multiline
                        rows={2}
                        placeholder="e.g. Product was faulty, customer changed their mind..."
                    />
                    <TextField
                        label="SKU (Optional)"
                        value={form.sku}
                        onChange={(e) =>
                            setForm({ ...form, sku: e.target.value })
                        }
                        fullWidth
                        placeholder="Defaults to the order number if left blank"
                    />
                    {error && (
                        <Typography variant="body2" color="error">
                            {error}
                        </Typography>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={saving}>
                    Cancel
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    color="warning"
                    disabled={saving || form.refund_amount <= 0}
                    startIcon={saving ? <CircularProgress size={16} /> : undefined}
                >
                    {saving ? "Refunding..." : "Refund"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
